import React, { useState } from "react";
import { Zap } from "lucide-react";
import { api, track } from "../lib/api";
import { useStore } from "../lib/store";

/* Razorpay Magic Checkout: phone → OTP → saved address → pay/COD, all inside Razorpay's window. */
export function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve; s.onerror = () => reject(new Error("Couldn't load Razorpay"));
    document.body.appendChild(s);
  });
}

export default function MagicCheckoutButton({ className = "", compact = false, onBeforeOpen }) {
  const { cart, coupon, clearCart, settings, subtotal } = useStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!settings?.integrations?.razorpay_configured || !cart.length) return null;

  const start = async () => {
    setBusy(true); setError("");
    try {
      onBeforeOpen?.();
      track("begin_checkout", { value: subtotal, currency: "INR", method: "magic" });
      const items = cart.map((c) => ({ product_id: c.product_id, variant_id: c.variant_id, qty: c.qty }));
      const { data } = await api.post("/checkout/magic", { items, coupon: coupon?.preview ? coupon.code : null });
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: data.payment.key_id,
        order_id: data.payment.razorpay_order_id,
        one_click_checkout: true,
        show_coupons: false,
        name: "Haven Hives",
        description: `Order ${data.order_number}`,
        image: `${window.location.origin}/brand/mark.png`,
        theme: { color: "#072835" },
        handler: async (resp) => {
          try {
            const r = await api.post("/payments/razorpay/magic-complete", resp);
            clearCart(); sessionStorage.removeItem("hh_checkout_form");
            const q = r.data.payment_status === "cod" ? `token=${r.data.verify_token}` : `tx_id=${r.data.tx_id}`;
            window.location.href = `/order-confirm/${r.data.order_number}?${q}`;
          } catch {
            window.location.href = `/payment/success?tx_id=${data.payment.tx_id}`;
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", () => setError("Payment didn't go through — please try again."));
      rzp.open();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Couldn't start express checkout.");
      setBusy(false);
    }
  };

  return (
    <div className={className} data-testid="magic-checkout">
      <button type="button" onClick={start} disabled={busy} data-testid="magic-checkout-btn"
        className={`w-full inline-flex items-center justify-center gap-2 rounded-full font-semibold bg-forest text-white hover:bg-forest transition-colors disabled:opacity-60 ${compact ? "py-3 text-sm" : "py-3.5"}`}>
        <Zap className="w-4 h-4 text-butter" /> {busy ? "Opening secure checkout…" : "Express checkout — phone & OTP"}
      </button>
      {!compact && <p className="text-[11px] text-charcoal/55 mt-2 text-center">Enter your mobile, verify with OTP and your saved address appears automatically. UPI, cards, net banking or Cash on Delivery.</p>}
      {error && <p role="alert" className="text-xs text-coral font-semibold mt-2" data-testid="magic-checkout-error">{error}</p>}
    </div>
  );
}
